'use client'

import i18next, { type KeyPrefix } from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'
import {
  type FallbackNs,
  initReactI18next,
  Trans as TransOriginal,
  type UseTranslationOptions,
  useTranslation as useTranslationOriginal,
} from 'react-i18next'

import { type LanguageId, LanguageIds } from '@mntn-dev/domain-types'
import { runningOnServer } from '@mntn-dev/utilities'

import { capitalizeFormatter } from './formatters/capitalize.ts'
import { getOptions } from './get-options.ts'
import { i18nLogger } from './logger.ts'
import { cookieName, fallbackLanguageId } from './settings.ts'
import type { TranslationNamespace } from './types.ts'
import { addFormatter } from './utilities/add-formatter.ts'
import { changeLanguage } from './utilities/change-language.ts'
import { getLocale } from './utilities/get-locale.ts'

export const Trans = TransOriginal

// initialize the i18next instance

i18next
  .use(initReactI18next)
  // disable LanguageDetector to force English
  // we can bring this back when we are actually ready to support spanish
  // .use(LanguageDetector)
  .use(
    resourcesToBackend(
      (language: string, namespace: string) =>
        import(`./locales/${language}/${namespace}.json`)
    )
  )
  .init({
    ...getOptions(),
    lng: fallbackLanguageId,
    fallbackLng: fallbackLanguageId,
    // Remove client side language detection config to force English
    // detection: {
    //   order: ['path', 'htmlTag', 'cookie', 'navigator'],
    // },
    preload: runningOnServer() ? LanguageIds : [],
    interpolation: { escapeValue: false },
  })
  .catch((error) => {
    i18nLogger.error('Error initializing i18next', { error })
  })

addFormatter(i18next, 'capitalize', capitalizeFormatter)

/**
 * Returns a `t` translation function and an `i18n` object.
 *
 * This function is a wrapper around `react-i18next`'s `useTranslation` hook.
 *
 * @param ns TranslationNamespace
 * @param options TranslationOptions
 * @returns An object with a `t` function and an `i18n` object
 */
export function useTranslation<
  Ns extends TranslationNamespace,
  KPrefix extends KeyPrefix<FallbackNs<Ns>> = undefined,
>(ns?: Ns, options?: UseTranslationOptions<KPrefix>) {
  const wrappedResult = useTranslationOriginal<Ns, KPrefix>(ns, options)
  return wrappedResult
}

export const useLanguage = () => useTranslation().i18n.language as LanguageId

export const useLocale = () => getLocale(useLanguage())

export const useChangeLanguage = (lng: LanguageId) => {
  const [cookies, setCookie] = useCookies([cookieName])
  const wrappedResult = useTranslationOriginal()

  const { i18n } = wrappedResult

  useEffect(() => {
    if (!lng || i18n.resolvedLanguage === lng) {
      return
    }

    changeLanguage(i18n, lng)
  }, [lng, i18n])

  useEffect(() => {
    if (cookies.i18next === lng) {
      return
    }
    setCookie(cookieName, lng, { path: '/' })
  }, [lng, cookies.i18next, setCookie])

  if (runningOnServer() && lng && i18n.resolvedLanguage !== lng) {
    changeLanguage(i18n, lng)
  }

  return wrappedResult
}

export function LanguageChanger({ lng }: { lng: LanguageId }) {
  useChangeLanguage(lng)
  return null
}
